import { useRef, type MouseEvent, type PointerEvent } from 'react';

const HOLD_MS = 520;
const MOVE_PX = 12;

/** Mantiene pulsado (o clic derecho) para abrir una acción, sin disparar al desplazar. */
export function useLongPress(onLongPress?: () => void): {
  onPointerDown?: (e: PointerEvent) => void;
  onPointerMove?: (e: PointerEvent) => void;
  onPointerUp?: () => void;
  onPointerCancel?: () => void;
  onClickCapture?: (e: MouseEvent) => void;
  onContextMenu?: (e: MouseEvent) => void;
} {
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);

  const clear = () => {
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = null;
    origin.current = null;
  };

  if (!onLongPress) return {};

  const armSuppressClick = () => {
    suppressClick.current = true;
    window.setTimeout(() => {
      suppressClick.current = false;
    }, 400);
  };

  const ignorable = (target: EventTarget | null) => {
    const el = target instanceof Element ? target : null;
    return Boolean(
      el?.closest(
        'input, textarea, .chat-bubble__thumbs-down, .chat-bubble__ticks-btn, .chat-image-lightbox'
      )
    );
  };

  return {
    onPointerDown: (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (ignorable(e.target)) return;
      if (timer.current != null) window.clearTimeout(timer.current);
      origin.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => {
        timer.current = null;
        origin.current = null;
        armSuppressClick();
        onLongPress();
      }, HOLD_MS);
    },
    onPointerMove: (e) => {
      if (!origin.current) return;
      if (Math.hypot(e.clientX - origin.current.x, e.clientY - origin.current.y) > MOVE_PX) clear();
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    onClickCapture: (e: MouseEvent) => {
      if (!suppressClick.current) return;
      suppressClick.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
    onContextMenu: (e) => {
      if (ignorable(e.target)) return;
      e.preventDefault();
      armSuppressClick();
      onLongPress();
    },
  };
}
